import React, { useEffect, useState } from 'react';
import {
  useSearchTermLists,
  useCreateSearchTermList,
  useSearchTerms,
  SearchTermList,
} from '../../../hooks/useSearchTerms';
import { useProject } from '../../../hooks/useProject';
import { SubmitButton } from '../../../components/SubmitButton';
import { Modal } from '../../../components/Modal';

const MAX_LINES = 1000;

interface Props {
  projectId: string;
  onClose: () => void;
  onCreated: (list: SearchTermList) => void;
}

export function AddListModal({ projectId, onClose, onCreated }: Props) {
  const { data: project } = useProject(projectId);
  const { data: listsData } = useSearchTermLists(projectId);
  const createList = useCreateSearchTermList(projectId);

  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [termsText, setTermsText] = useState('');

  const { data: templateTermsData } = useSearchTerms(projectId, templateId);

  useEffect(() => {
    if (!templateId || !templateTermsData) return;
    setTermsText(templateTermsData.searchTerms.map(t => t.term).join('\n'));
  }, [templateId, templateTermsData]);

  const enforceLineLimit = (value: string) => {
    const lines = value.split('\n');
    return lines.length > MAX_LINES ? lines.slice(0, MAX_LINES).join('\n') : value;
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const terms = termsText.split('\n').map(s => s.trim()).filter(Boolean);
    createList.mutate(
      { name: name.trim(), terms: terms.length ? terms : undefined },
      { onSuccess: list => { onCreated(list); onClose(); } }
    );
  };

  const lists = listsData?.searchTermLists ?? [];

  return (
    <Modal
      title="Add Search Term List"
      icon="fas fa-credit-card"
      testId="admin-search-terms-add-modal"
      onClose={onClose}
      onSubmit={handleSave}
      dialogStyle={{ width: 560 }}
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose} data-testid="admin-search-terms-add-cancel-btn">Cancel</button>
          <SubmitButton
            pending={createList.isPending}
            pendingLabel="Saving..."
            disabled={!name.trim()}
            data-testid="admin-search-terms-add-submit-btn"
          >Save</SubmitButton>
        </>
      }
    >
      <div className="form-group">
        <h4>Project: <small><i className="fas fa-gavel" aria-hidden="true" /> {project?.name}</small></h4>
      </div>
      <div className="form-group">
        <input
          type="text"
          autoComplete="off"
          placeholder="Search Term List Name"
          data-testid="admin-search-terms-add-name-input"
          className="form-control"
          value={name}
          onChange={e => setName(e.target.value)}
          required
          maxLength={255}
          disabled={createList.isPending}
        />
      </div>
      <div className="form-group">
        <select
          className="form-control"
          data-testid="admin-search-terms-add-template-select"
          value={templateId}
          onChange={e => setTemplateId(e.target.value)}
          disabled={createList.isPending}
        >
          <option value="">-- import terms from previous search term list --</option>
          {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>
      <div className="form-group">
        <textarea
          className="form-control"
          placeholder="List of search terms"
          data-testid="admin-search-terms-add-terms-input"
          rows={10}
          value={termsText}
          onChange={e => setTermsText(enforceLineLimit(e.target.value))}
          disabled={createList.isPending}
        />
      </div>
    </Modal>
  );
}
